import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { Loader2 } from "lucide-react";

/* Mobile/touch PDF renderer — replaces the browser's native iframe PDF plugin
   (broken on mobile: page 1 only, no scroll). Pages are VIRTUALIZED: every
   page gets a correctly-sized placeholder instantly, and canvases render only
   near the viewport (IntersectionObserver), with far pages demoted back to
   placeholders — so a 31-page scanned board paper never exhausts mobile
   canvas memory. Canvas size is capped for iOS limits. Vertical scroll pans;
   page-level pinch zoom remains available (viewport meta permits scaling). */

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.js",
  import.meta.url
).toString();

const PIXEL_BOOST = 2; // 2x keeps text sharp without blowing mobile memory
const MAX_CANVAS_DIM = 2200; // conservative iOS canvas dimension cap
const KEEP_DISTANCE = 4; // pages kept rendered on each side of the viewport

const PdfViewer = ({ blob }) => {
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [pageInfo, setPageInfo] = useState({ current: 1, total: 0 });

  useEffect(() => {
    let cancelled = false;
    let pdf = null;
    let renderObserver = null;
    let currentObserver = null;
    const renderedPages = new Map(); // pageNum -> canvas

    const renderPage = async (pageNum, slot) => {
      if (cancelled || renderedPages.has(pageNum) || slot.dataset.busy) return;
      slot.dataset.busy = "1";
      try {
        const page = await pdf.getPage(pageNum);
        const fitWidth = slot.clientWidth || 360;
        const base = page.getViewport({ scale: 1 });
        let scale = (fitWidth / base.width) * PIXEL_BOOST;
        while (
          scale > 0.4 &&
          (base.width * scale > MAX_CANVAS_DIM || base.height * scale > MAX_CANVAS_DIM * 2)
        ) {
          scale *= 0.8;
        }
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.cssText =
          "width:100%;height:auto;display:block;border-radius:4px;background:#fff";
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        if (cancelled) return;
        slot.style.paddingBottom = "0";
        slot.innerHTML = "";
        slot.appendChild(canvas);
        renderedPages.set(pageNum, canvas);

        // Demote far-away pages back to placeholders to cap memory
        for (const n of [...renderedPages.keys()]) {
          if (Math.abs(n - pageNum) <= KEEP_DISTANCE) continue;
          const farSlot = bodyRef.current?.querySelector(`[data-page="${n}"]`);
          const c = renderedPages.get(n);
          if (farSlot && c) {
            farSlot.innerHTML = "";
            farSlot.style.paddingBottom = `${(c.height / c.width) * 100}%`;
            delete farSlot.dataset.busy;
            renderedPages.delete(n);
            renderObserver?.observe(farSlot);
          }
        }
      } catch (e) {
        if (!cancelled && slot.isConnected) {
          slot.style.paddingBottom = "0";
          slot.innerHTML = `<div style="color:#fca5a5;font-size:12px;font-weight:600;padding:20px;text-align:center">Page ${pageNum} couldn't be rendered — try Download instead.</div>`;
        }
      }
    };

    (async () => {
      try {
        const data = await blob.arrayBuffer();
        pdf = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled || !bodyRef.current) return;
        const body = bodyRef.current;
        body.innerHTML = "";

        renderObserver = new IntersectionObserver(
          (entries) => {
            for (const en of entries) {
              if (en.isIntersecting) {
                renderObserver.unobserve(en.target);
                renderPage(Number(en.target.dataset.page), en.target);
              }
            }
          },
          { root: scrollRef.current, rootMargin: "1200px 0px" }
        );
        currentObserver = new IntersectionObserver(
          (entries) => {
            for (const en of entries) {
              if (en.isIntersecting) {
                setPageInfo((p) => ({ ...p, current: Number(en.target.dataset.page) }));
              }
            }
          },
          { root: scrollRef.current, rootMargin: "-30% 0px -60% 0px" }
        );

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const slot = document.createElement("div");
          slot.dataset.page = String(i);
          slot.dataset.testid = `pyq-pdf-page-${i}`;
          slot.style.cssText = `position:relative;width:100%;padding-bottom:${(base.height / base.width) * 100}%;margin-bottom:8px;background:rgba(255,255,255,0.07);border-radius:4px`;
          body.appendChild(slot);
          renderObserver.observe(slot);
          currentObserver.observe(slot);
        }
        setPageInfo({ current: 1, total: pdf.numPages });
        setStatus("ready");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      renderObserver?.disconnect();
      currentObserver?.disconnect();
      if (bodyRef.current) bodyRef.current.innerHTML = "";
      if (pdf) pdf.destroy();
    };
  }, [blob]);

  return (
    <div className="relative flex w-full flex-1 flex-col overflow-hidden">
      {status === "ready" && (
        <span
          data-testid="pyq-pdf-counter"
          className="absolute right-3 top-2 z-10 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-bold text-white"
        >
          {pageInfo.current} / {pageInfo.total}
        </span>
      )}
      <div
        ref={scrollRef}
        data-testid="pyq-pdf-pages"
        className="w-full flex-1 overflow-y-auto overscroll-contain bg-[#525659] p-2"
        style={{ touchAction: "pan-y pinch-zoom", WebkitOverflowScrolling: "touch" }}
      >
        {status === "loading" && (
          <div className="flex flex-col items-center gap-2 p-10" data-testid="pyq-pdf-loading">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
            <p className="text-xs font-semibold text-white/70">Preparing paper…</p>
          </div>
        )}
        {status === "error" && (
          <p
            data-testid="pyq-pdf-error"
            className="p-6 text-center text-sm font-semibold text-white"
          >
            Could not display this PDF here — use Download to open it in your
            device's PDF app.
          </p>
        )}
        <div ref={bodyRef} className="mx-auto w-full" />
      </div>
    </div>
  );
};

export default PdfViewer;
